/**
 * Auth context.
 *
 * The session lives entirely in httpOnly cookies the browser sends for us, so
 * this context holds no token and never touches localStorage. It holds only the
 * identity the API reports, which it learns by asking `GET /auth/me`. A 401 is
 * the normal signed-out answer, not an error to surface.
 *
 * Phase 1 wires the plumbing. The login screen and the protected-route guard
 * are T2.11 in Phase 2 — deliberately not built here.
 */
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';

/**
 * The user's GLOBAL role (design.md § 5). Canonical keys — never renamed by
 * terminology overrides.
 *
 * Deliberately NOT the same type as BlueprintRole in ./blueprints. Global role
 * answers "may this person administer the system"; a blueprint role answers
 * "what may they do with this one blueprint". They share the label 'admin' and
 * nothing else, and conflating them is what let an admin fail to reach an admin
 * screen at Gate G2.
 */
export type GlobalRole = 'admin' | 'user';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  globalRole: GlobalRole;
  mustChangePassword: boolean;
}

/**
 * The API wraps the user in an envelope: { user: { … } }. Unwrapping it is not
 * optional — handing the envelope straight to the context leaves every field
 * undefined while `Boolean(user)` stays true, so the session looks valid and
 * every role check silently fails closed. That is exactly what Gate G2 found.
 */
interface UserEnvelope {
  user: CurrentUser;
}

export interface Credentials {
  email: string;
  password: string;
}

export interface AuthState {
  user: CurrentUser | null;
  /** True until the first /auth/me has settled — render nothing role-dependent before then. */
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (credentials: Credentials) => Promise<CurrentUser>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export const CURRENT_USER_KEY = ['auth', 'me'] as const;

async function fetchCurrentUser(signal?: AbortSignal): Promise<CurrentUser | null> {
  try {
    return (await api.get<UserEnvelope>('/auth/me', signal)).user;
  } catch (error) {
    // Signed out is a normal state, not a failure.
    if (error instanceof ApiError && error.isUnauthorised) return null;
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: CURRENT_USER_KEY,
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    // A 401 here is an answer, not a transient fault.
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const signInMutation = useMutation({
    mutationFn: (credentials: Credentials) =>
      api.post<UserEnvelope>('/auth/login', credentials).then((r) => r.user),
    onSuccess: (user) => {
      queryClient.setQueryData(CURRENT_USER_KEY, user);
    },
  });

  const signOutMutation = useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      // Clear regardless of the server's answer: the local cache must not keep
      // a signed-in identity, and every cached blueprint belonged to that session.
      queryClient.setQueryData(CURRENT_USER_KEY, null);
      void queryClient.clear();
    },
  });

  const signIn = useCallback(
    (credentials: Credentials) => signInMutation.mutateAsync(credentials),
    [signInMutation],
  );

  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync();
  }, [signOutMutation]);

  const value = useMemo<AuthState>(
    () => ({
      user: data ?? null,
      isLoading: isPending,
      isAuthenticated: Boolean(data),
      signIn,
      signOut,
    }),
    [data, isPending, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
